/**
 * Copyright (c) 2022 Cedalo GmbH
 */
import { formatDateString } from '@cedalo/webui/src/components/base/listing/Utils';
import { ConnectionIcon } from '@cedalo/webui/src/ui/sheeteditor/connections/ConnectionIcon';
import StreamSheetPageTitle from '@cedalo/webui/src/ui/utils/StreamSheetPageTitle';
import TableSortHeader from '@cedalo/webui/src/ui/utils/TableSortHeader';
import { Box, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { injectIntl } from 'react-intl';

function AccountsTableComponent(props) {
	const { filter, accountId, onSelectAccount, delegate } = props;
	const [sortBy, setSortBy] = useState('lastModified');
	const [sortDirection, setSortDirection] = useState('desc');
	const [accounts, setAccounts] = useState([]);

	// useEffect(() => delegate.onComponentLoad({ setAccounts }), []);
	useEffect(() => {
		const run = async () => setAccounts(await delegate.getAccounts());
		run();
	}, []);

	const [subData, subErrors] = delegate.useAccountsSubscription();

	useEffect(() => {
		if (subErrors) {
			console.error(subErrors);
		}
	}, [subErrors]);
	useEffect(() => {
		if (subData) {
			const eventData = subData.accountChanged;
			switch (eventData.__typename) {
				case 'AccountCreateEvent': {
					setAccounts((current) => [...current, eventData.account]);
					break;
				}
				case 'AccountUpdateEvent': {
					setAccounts((current) =>
						current.map((a) => (a.id === eventData.accountId ? { ...a, ...eventData } : a))
					);
					break;
				}
				case 'AccountDeleteEvent': {
					setAccounts((current) => current.filter(({ id }) => id !== eventData.accountId));
					break;
				}
				default:
			}
		}
	}, [subData]);

	const handleSort = (event, property) => {
		const orderBy = property;
		const order = (sortBy === property && sortDirection === 'desc') || sortBy !== property ? 'asc' : 'desc';
		setSortBy(orderBy);
		setSortDirection(order);
	};

	const handleSelection = (resource) => {
		onSelectAccount(resource.id);
	};

	const getAccounts = () => {
		let rows = [...accounts];

		if (filter && filter.length > 0) {
			rows = rows.filter(
				(resource) =>
					resource.name.toLowerCase().includes(filter) || resource.type.toLowerCase().includes(filter)
			);
		}
		rows.sort((a, b) => {
			const dir = sortDirection === 'asc' ? 1 : -1;

			switch (sortBy) {
				case 'name': {
					const aName = a[sortBy] || '';
					const bName = b[sortBy] || '';
					if (aName.toLowerCase() > bName.toLowerCase()) {
						return dir;
					} else if (aName.toLowerCase() < bName.toLowerCase()) {
						return -1 * dir;
					}
					return 0;
				}
				case 'type': {
					const aType = a[sortBy] || '';
					const bType = b[sortBy] || '';
					if (aType.toLowerCase() > bType.toLowerCase()) {
						return dir;
					} else if (aType.toLowerCase() < bType.toLowerCase()) {
						return -1 * dir;
					}
					return 0;
				}
				case 'lastModified': {
					const aLastModified = a.lastModified || new Date().toISOString();
					const bLastModified = b.lastModified || new Date().toISOString();
					const res = new Date(aLastModified) - new Date(bLastModified);
					return dir * res;
				}
				default:
					return 0;
			}
		});
;
		return rows;
	};

	return (
		<Box sx={{ display: 'grid', height: 'calc(100%)', gridTemplateRows: 'max-content max-content auto' }}>
			<StreamSheetPageTitle
				title={props.intl.formatMessage({ id: delegate.getIntlKey('Table.Title') }, {})}
				subTitle={props.intl.formatMessage({ id: delegate.getIntlKey('Table.SubTitle') }, {})}
			/>
			<TableContainer>
				<Table stickyHeader size="medium" aria-label="collapsible table">
					<TableSortHeader
						height={48}
						cells={[
							{
								id: 'gap',
								numeric: false,
								padding: false,
								label: '',
								width: '0%',
								minWidth: '8px'
							},
							{ id: 'name', numeric: false, padding: false, label: 'Name', width: '25%' },
							{
								id: 'type',
								numeric: false,
								padding: false,
								label: delegate.getIntlKey('Table.Header.Type'),
								width: '15%',
								minWidth: '150px'
							},
							// {
							// 	id: 'url',
							// 	hideOnSmallDisplays: true,
							// 	numeric: false,
							// 	label: 'Streams.URL',
							// 	width: '35%'
							// },
							{
								id: 'lastModified',
								hideOnSmallDisplays: true,
								numeric: false,
								label: 'LastModified',
								width: '17%'
							}
						]}
						orderBy={sortBy}
						order={sortDirection}
						onRequestSort={handleSort}
						// onFieldToggle={(field, state) => this.onFieldToggle(field, state)}
					/>
					<TableBody>
						{getAccounts().map((resource) => (
							<TableRow
								style={{
									height: '40px',
									cursor: 'pointer'
								}}
								hover
								tabIndex={-1}
								selected={accountId === resource.id}
								key={`${resource.name}`}
							>
								<TableCell onClick={() => handleSelection(resource)} padding="none" />
								<TableCell
									style={{ fontWeight: 'bold' }}
									onClick={() => handleSelection(resource)}
									padding="none"
									component="th"
									scope="row"
								>
									<div
										sx={{
											display: 'flex',
											justifyContent: 'space-between',
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis'
										}}
									>
										<div style={{ lineHeight: '29px' }}>{resource.name}</div>
									</div>
								</TableCell>
								<TableCell onClick={() => handleSelection(resource)} padding="none">
									<div style={{ display: 'grid', gridGap: '8px', gridTemplateColumns: '24px 1fr ' }}>
										<ConnectionIcon src={resource.logo} />
										<div style={{ alignSelf: 'center' }}>{resource.type}</div>
									</div>
								</TableCell>
								<TableCell
									sx={{
										display: {
											xs: 'none',
											sm: 'none',
											md: 'none',
											lg: 'table-cell',
											xl: 'table-cell'
										}
									}}
									onClick={() => handleSelection(resource)}
									padding="none"
								>
									{formatDateString(new Date(resource.lastModified).toISOString())}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Box>
	);
}

export const AccountsTable = injectIntl(AccountsTableComponent);
